// buildingInProduction.js
import React from "react";
import { useNavigate } from "react-router-dom";
import { useBuildingProduction } from "../../../hooks/useBuildingProduction";
import useTableSort from "../../../hooks/useTableSort";
import SortableTableHeader from "../../modalContent/SortableTableHeader";

// ─── Component ────────────────────────────────────────────────────────────────

const PROPERTY_COL_COUNT = 2; // Property | Total SQFT
const MODULES_COL_COUNT = 3; // Property | Work Order | SQFT

const BuildingInProduction = () => {
  const navigate = useNavigate();
  const { modules, propertyTotals, loading, error } = useBuildingProduction();

  // ── Table sorting ─────────────────────────────────────────────────────────
  // Property table sorting
  const {
    sortedData: sortedPropertyTotals,
    requestSort: requestPropertySort,
    getSortClass: getPropertySortClass,
  } = useTableSort(propertyTotals, {
    initialKey: "property",
    initialDirection: "asc",
  });

  // Modules table sorting
  const {
    sortedData: sortedModules,
    requestSort: requestModulesSort,
    getSortClass: getModulesSortClass,
  } = useTableSort(modules, {
    initialKey: "Property",
    initialDirection: "asc",
  });

  // ── Row click ─────────────────────────────────────────────────────────────
  const handleRowClick = (workOrder) => {
    navigate(`/module-metrics?workOrder=${encodeURIComponent(workOrder)}`);
  };

  // ── Shared tbody helpers ──────────────────────────────────────────────────
  const LoadingRow = ({ colSpan }) => (
    <tr>
      <td colSpan={colSpan} className="modal-table-cell--center">
        Loading data…
      </td>
    </tr>
  );

  const ErrorRow = ({ colSpan }) => (
    <tr>
      <td
        colSpan={colSpan}
        className="modal-table-cell--center modal-table-cell--error"
      >
        {error}
      </td>
    </tr>
  );

  const EmptyRow = ({ colSpan, message = "No data found." }) => (
    <tr>
      <td colSpan={colSpan} className="modal-table-cell--center">
        {message}
      </td>
    </tr>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <p className="modal-description">
        Detailed breakdown of properties currently in production
      </p>

      {/* ── Table 1: SQFT by Property ──────────────────────────────────── */}
      <div className="modal-section modal-section--scroll">
        <h3>Properties and Total Square Footage</h3>
        <div className="modal-table-scroll">
          <table className="modal-table">
            <thead>
              <tr>
                <SortableTableHeader
                  columnKey="property"
                  onSort={requestPropertySort}
                  sortClass={getPropertySortClass("property")}
                >
                  Property
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="totalSqft"
                  onSort={requestPropertySort}
                  sortClass={getPropertySortClass("totalSqft")}
                >
                  Total Sq Ft in Progress
                </SortableTableHeader>
              </tr>
            </thead>
            <tbody>
              {loading && <LoadingRow colSpan={PROPERTY_COL_COUNT} />}
              {!loading && error && <ErrorRow colSpan={PROPERTY_COL_COUNT} />}
              {!loading && !error && sortedPropertyTotals.length === 0 && (
                <EmptyRow colSpan={PROPERTY_COL_COUNT} />
              )}
              {!loading &&
                !error &&
                sortedPropertyTotals.map((row) => (
                  <tr key={row.property}>
                    <td>{row.property}</td>
                    <td>{row.totalSqft.toLocaleString()}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Table 2: All modules ───────────────────────────────────────── */}
      <div className="modal-section modal-section--scroll">
        <h3>Items in Production</h3>
        <div className="modal-table-scroll">
          <table className="modal-table modal-table--clickable">
            <thead>
              <tr>
                <SortableTableHeader
                  columnKey="Property"
                  onSort={requestModulesSort}
                  sortClass={getModulesSortClass("Property")}
                >
                  Property
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="WoNum"
                  onSort={requestModulesSort}
                  sortClass={getModulesSortClass("WoNum")}
                >
                  Work Order
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="Sqft"
                  onSort={requestModulesSort}
                  sortClass={getModulesSortClass("Sqft")}
                >
                  Sq Ft Per Item
                </SortableTableHeader>
              </tr>
            </thead>
            <tbody>
              {loading && <LoadingRow colSpan={MODULES_COL_COUNT} />}
              {!loading && error && <ErrorRow colSpan={MODULES_COL_COUNT} />}
              {!loading && !error && sortedModules.length === 0 && (
                <EmptyRow colSpan={MODULES_COL_COUNT} />
              )}
              {!loading &&
                !error &&
                sortedModules.map((mod) => (
                  <tr
                    key={mod.WoNum}
                    onClick={() => handleRowClick(mod.WoNum)}
                    className="modal-table-row--clickable"
                    title="Click to view module details"
                  >
                    <td>{mod.Property}</td>
                    <td>{mod.WoNum}</td>
                    <td>
                      {mod.Sqft != null
                        ? Number(mod.Sqft).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default BuildingInProduction;
